import { guided } from './guidedRuntime';
import { PLAN_SYSTEM_PROMPT } from './prompt/plan-prompt';
import type { Plan, LayoutOption, StyleBrief, NavKind, MainLayout, PatternId } from './planTypes';

const NAVS: NavKind[] = ['tabs', 'stack', 'single', 'drawer'];
const LAYOUTS: MainLayout[] = ['list', 'feed', 'grid', 'detail', 'form', 'dashboard'];
const isHex = (s: unknown): s is string => typeof s === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(s);

const RADII = ['sharp', 'soft', 'round'] as const;
const DENSITIES = ['tight', 'comfortable', 'airy'] as const;
const SCALES = ['restrained', 'bold', 'hero'] as const;
const PATTERNS: PatternId[] = ['two-tone-headings', 'floating-tabbar', 'icon-chips', 'segmented-range', 'gradient-chart', 'peek-carousel', 'glass-overlay', 'paired-stats'];
const pickEnum = <T extends string>(v: unknown, allowed: readonly T[], dflt: T): T => (allowed.includes(v as T) ? (v as T) : dflt);

export function normalizeStyle(raw: unknown): StyleBrief {
  const r = (raw ?? {}) as Record<string, unknown>;
  const p = (r.palette ?? {}) as Record<string, unknown>;
  return {
    palette: {
      primary: isHex(p.primary) ? p.primary : '#6C5CE7',
      accent: isHex(p.accent) ? p.accent : undefined,
      background: isHex(p.background) ? p.background : '#FFFFFF',
      text: isHex(p.text) ? p.text : '#1A1A1A',
    },
    mood: typeof r.mood === 'string' ? r.mood : '',
    keywords: Array.isArray(r.keywords) ? r.keywords.filter((k) => typeof k === 'string').slice(0, 6) : [],
    radius: pickEnum(r.radius, RADII, 'soft'),
    density: pickEnum(r.density, DENSITIES, 'comfortable'),
    typeScale: pickEnum(r.typeScale, SCALES, 'bold'),
    patterns: Array.isArray(r.patterns) ? (r.patterns.filter((x) => PATTERNS.includes(x as PatternId)) as PatternId[]) : [],
  };
}

function normalizeOption(raw: unknown, i: number): LayoutOption | null {
  const r = (raw ?? {}) as Record<string, unknown>;
  if (typeof r.title !== 'string' && typeof r.blueprint !== 'string') return null;
  const nav = NAVS.includes(r.nav as NavKind) ? (r.nav as NavKind) : 'stack';
  const tabs = Array.isArray(r.tabs) ? r.tabs.filter((t) => typeof t === 'string').slice(0, 5) : undefined;
  return {
    id: typeof r.id === 'string' && r.id ? r.id : `opt-${i + 1}`,
    title: typeof r.title === 'string' ? r.title : `Option ${i + 1}`,
    blueprint: typeof r.blueprint === 'string' ? r.blueprint : (typeof r.title === 'string' ? r.title : ''),
    nav,
    tabs: nav === 'tabs' || nav === 'drawer' ? tabs : undefined,
    mainLayout: LAYOUTS.includes(r.mainLayout as MainLayout) ? (r.mainLayout as MainLayout) : 'list',
    screens: Array.isArray(r.screens) ? r.screens.filter((s) => typeof s === 'string').slice(0, 6) : [],
  };
}

/** Parse the planning model's reply into a Plan, tolerating stray prose/fences. Returns
 *  null when there's no usable plan (caller falls back to a direct build). */
export function parsePlan(content: string): Plan | null {
  const match = (content ?? '').match(/\{[\s\S]*\}/);
  if (!match) return null;
  try {
    const raw = JSON.parse(match[0]) as Record<string, unknown>;
    if (!raw.styleBrief || !Array.isArray(raw.options)) return null;
    const options = raw.options.slice(0, 3).map(normalizeOption).filter((o): o is LayoutOption => !!o);
    if (!options.length) return null;
    return { styleBrief: normalizeStyle(raw.styleBrief), options };
  } catch {
    return null;
  }
}

/** Run the pre-build planning pass: extract a style brief from the (optional) vibe image
 *  and propose 2–3 layout blueprints. Returns null on any failure → direct build. */
export async function planLayouts(input: { goal: string; usage?: string }, image?: string): Promise<Plan | null> {
  const user = `App goal: ${input.goal}` +
    (input.usage ? `\nHow people will use it: ${input.usage}` : '') +
    `\n\nReturn the JSON plan now.`;
  try {
    const res = await guided().generate({
      systemPrompt: PLAN_SYSTEM_PROMPT,
      messages: [{ role: 'user', content: user }],
      signedIn: true,
      structured: true, // capable model (vision when an image is attached)
      ...(image ? { image } : {}),
    });
    return parsePlan(res.content ?? '');
  } catch {
    return null;
  }
}
